import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, UrlSegment} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from '../../../../auth/auth.service';
import {ConfirmationService} from 'primeng/api';
import {Location} from '@angular/common';
import {permissions} from '../../../../permissions';
import {ScannedDataService} from '../../services/scanned-data.service';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
    templateUrl: './scanned-data-form.component.html',
    styleUrls: ['./scanned-data-form.component.scss'],
    providers: [
        AuthService,
        ScannedDataService
    ]
})
export class ScannedDataFormComponent implements OnInit, OnDestroy {

    private static charCodeTab = 9;
    private static charCodeNewLine = 10;
    private static charCodeCarriageReturn = 13;
    private static charCodeComma = 44;

    @ViewChild('searchTextArea') searchTextArea: ElementRef;

    public permissions = permissions;

    public action = '';

    public loading: boolean;
    public isWorkspaceSelected: boolean;
    public editMode: boolean;
    public returnEditToView: boolean;
    public editEnable: boolean;

    public ID: number;
    public mode: string;
    public screenTitle: string;

    public searchBox: string;
    public searchBoxDisable: boolean;
    public record: any;
    public changedRecord: any;

    public parts: Array<any>;
    private partsCopy: Array<any>;
    public errors: Array<any>;

    private subscription: Subscription;
    private scannedDataServiceRef = ScannedDataService;

    public uploadFlagOptions: Array<object>;
    deleteConfirmation = '';

    constructor(
        private route: ActivatedRoute,
        private scannedDataService: ScannedDataService,
        private authService: AuthService,
        private confirmationService: ConfirmationService,
        private router: Router,
        private toast: ToastrService,
        private elementRef: ElementRef,
        private location: Location,
        private title: Title
    ) {
        this.title.setTitle('OES - ' + this.screenTitle + ' Manage');
    }

    ngOnInit() {
        this.isWorkspaceSelected = this.getSelectedWorkspace();
        this.initValues();
        this.parseUrl(this.route.snapshot.url);
        if (this.isWorkspaceSelected) {
            this.getData();
        }
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    initValues() {
        this.loading = false;
        this.searchBox = '';
        this.searchBoxDisable = false;

        this.record = {};
        this.changedRecord = {};
        this.errors = [];

        this.action = this.route.snapshot.url[2].toString();
        this.ID = (this.route.snapshot.params.id !== undefined && this.route.snapshot.params.id !== null)
            ? this.route.snapshot.params.id : 0;
        this.editMode = this.action !== 'view';
        this.returnEditToView = this.action === 'view';
        this.editEnable = false;

        this.parts = [];
        this.partsCopy = [];
    }

    getSelectedWorkspace() {
        return this.authService.workspaceMasterCustomerID !== null;
    }

    getData() {
        this.subscription = this.scannedDataService.getById(this.ID, this.mode).subscribe(result => {
            if (result.success) {
                this.record = result.data;
                this.record['QUANTITY'] = Number(this.record['QUANTITY']);
                this.cloneRecordToChangedRecord();
            } else {
                this.toast.error('Unable to load data. Please try later!', 'Error');
                setTimeout(() => {
                    this.loading = false;
                    this.backClick();
                }, 2000);
            }
        }, () => {
            this.toast.error('Request timed out', 'Service Error');
            setTimeout(() => {
                this.loading = false;
                this.backClick();
            }, 2000);
        });
    }

    private parseUrl(url: Array<UrlSegment>) {
        if (url[0].toString() === 'order-entry-system') {
            if (url[1].toString() === this.scannedDataServiceRef.URL_SD2) {
                this.screenTitle = this.scannedDataServiceRef.LABEL_SD2;
                this.mode = this.scannedDataServiceRef.MODE_SD2;
                this.uploadFlagOptions = this.scannedDataService.getConsumptionDataUploadFlags();
            } else if (url[1].toString() === this.scannedDataServiceRef.URL_SD3) {
                this.screenTitle = this.scannedDataServiceRef.LABEL_SD3;
                this.mode = this.scannedDataServiceRef.MODE_SD3;
                this.uploadFlagOptions = this.scannedDataService.getReceivingDataUploadFlags();
            } else {
                this.screenTitle = this.scannedDataServiceRef.LABEL_SD1;
                this.mode = this.scannedDataServiceRef.MODE_SD1;
                this.uploadFlagOptions = this.scannedDataService.getScannedDataUploadFlags();
                this.editEnable = true;
            }
        }
    }

    cancelClick() {
        this.cloneRecordToChangedRecord();
        if (this.action === 'add' || (this.action === 'edit' && !this.returnEditToView)) {
            this.backClick();
        } else {
            this.editMode = false;
            this.action = 'view';
            this.getData();
        }
    }

    cloneRecordToChangedRecord() {
        this.changedRecord = JSON.parse(JSON.stringify(this.record));
    }

    clearSearchBox() {
        this.searchBox = '';
        this.parts = [];
        this.partsCopy = [];
    }

    backClick() {
        this.loading = false;
        window.history.back();
    }

    editRecord() {
        this.editMode = true;
        this.action = 'edit';
        setTimeout(() => {
            this.searchTextArea.nativeElement.focus();
        }, 100);
    }

    addItems() {
        this.searchBoxDisable = true;
        let startIndex = 0;
        let search = '';
        for (let i = 0; i < this.searchBox.length; i++) {
            const charCode = this.searchBox.charCodeAt(i);
            if (
                Number(charCode) === ScannedDataFormComponent.charCodeNewLine
                || Number(charCode) === ScannedDataFormComponent.charCodeTab
                || Number(charCode) === ScannedDataFormComponent.charCodeComma
            ) {
                if (i > startIndex) {
                    const searchText = this.searchBox.substring(startIndex, i);
                    if (searchText.trim().length > 0) {
                        search = searchText.trim();
                    }
                    break;
                }
                startIndex = i + 1;
            } else if (i === (this.searchBox.length - 1)) {
                const searchText = this.searchBox.substring(startIndex, i + 1);
                if (searchText.trim().length > 0) {
                    search = searchText.trim();
                }
                break;
            }
        }

        if (search.length > 0) {
            this.loading = true;
            this.searchBox = this.searchBox.trim();
            this.findMatches(search);
        } else {
            this.searchBox = '';
            this.searchBoxDisable = false;
            setTimeout(() => {
                this.searchTextArea.nativeElement.focus();
            }, 200);
        }
    }

    onChangeSearchBox() {
        this.parts = [];
    }

    onOptionSelect(event) {
        // load part info into changedRecord
        const partIndex = Number(event.option.value);
        this.updateChangedRecord(this.partsCopy[partIndex]);
        this.searchBox = '';
        this.partsCopy = [];
    }

    private updateChangedRecord(part: any) {
        this.errors = [];
        const attributesToCopy = [
            'CO1_ID', 'CO1_NAME', 'CU0_ID', 'CU1_ID', 'CU1_NAME',
            'PL1_NAME', 'PL1_SHORT_CODE', 'RA1_SHIP_TO_ID', 'RA1_SHIP_TO_NAME',
            'CONTRACT_BIN_ID', 'ITEM_ID',
            'CUSTOMER_PART_NO', 'MD1_ID', 'MD1_MIN_QTY', 'MD1_PRICE', 'MD1_REORDER_QTY', 'MD1_UM', 'MD1_UM_SIZE'
        ];
        for (const attribute of attributesToCopy) {
            this.changedRecord[attribute] = part[attribute];
        }
        this.changedRecord['QUANTITY'] = Number(part['MD1_REORDER_QTY']);
        this.toast.info('Part selection updated.');
    }

    findMatches(searchText) {
        // get matches from server
        // if one match, update "changedRecord" - equivalent to end result of "onOptionSelect"
        // if multiple match, load "parts" and then will trigger "onOptionSelect"
        this.parts = [];
        this.partsCopy = [];
        this.scannedDataService.findMatches(searchText, this.mode).subscribe(result => {
            if (result.success) {
                if (result.data.length > 1) {
                    this.parts = result.data;
                    this.partsCopy = JSON.parse(JSON.stringify(this.parts));
                    setTimeout(() => {
                        this.searchTextArea.nativeElement.focus();
                    }, 150);
                } else if (result.data.length === 1) {
                    this.updateChangedRecord(result.data[0]);
                    this.searchBox = '';
                } else {
                    // no matches found
                    this.toast.error('No matches found for search text.', 'Error');
                }
            } else {
                this.searchBox = this.searchBox.trim();
                this.toast.error('Failed to get part info. Please try again later.', 'Error');
            }
            this.loading = false;
            this.searchBoxDisable = false;
        }, () => {
            this.toast.error('Request timed out.', 'Service Error');
            this.loading = false;
            this.searchBoxDisable = false;
        });
    }

    onTextAreaKeyDown(event) {
        const keyCode = Number(event.keyCode);
        if (keyCode === ScannedDataFormComponent.charCodeCarriageReturn) {
            setTimeout(() => {
                this.addItems();
            }, 500);
        } else if (keyCode === ScannedDataFormComponent.charCodeTab) {
            setTimeout(() => {
                this.searchBox += String.fromCharCode(ScannedDataFormComponent.charCodeTab);
                this.searchTextArea.nativeElement.focus();
            }, 150);
        }
    }

    increaseQty() {
        this.changedRecord['QUANTITY'] += Number(this.changedRecord['MD1_REORDER_QTY']);
    }

    decreaseQty() {
        this.changedRecord['QUANTITY'] -= Number(this.changedRecord['MD1_REORDER_QTY']);
        if (this.changedRecord['QUANTITY'] < Number(this.changedRecord['MD1_MIN_QTY'])) {
            this.changedRecord['QUANTITY'] = Number(this.changedRecord['MD1_MIN_QTY']);
        }
    }

    saveClick() {
        this.errors = [];
        this.loading = true;
        this.changedRecord['mode'] = this.mode;
        this.scannedDataService.save(this.changedRecord).subscribe(result => {
            if (result.success) {
                this.toast.success('Record updated.', 'Success');
                this.cancelClick();
            } else {
                this.errors = result.errors;
            }
            this.loading = false;
        }, () => {
            this.toast.error('Request timed out', 'Service Error');
            this.loading = false;
        });
    }

}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/scanned-data/scanned-data-form/scanned-data-form.component.ts